"use client";

import React from 'react';

export interface TypographyColorsBlockProps {
    subtitle?: string;
    title?: string;
    description?: string;
    systemLabel?: string;
    
    // Fonts
    fonts?: {
        tag?: string;
        letters?: string;
        description?: string;
        fontFamilyClass?: string; // e.g. 'font-cormorant' or 'font-sans'
    }[];
    
    // Colors
    colors?: {
        name?: string;
        hex?: string;
        description?: string;
        colorValue?: string; // e.g. '#FAF7F2'
    }[];
    
    style?: {
        backgroundColor?: string;
        textColor?: string;
        accentColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
    };
}

export default function TypographyColorsBlock({
    subtitle = "03 / DESIGN STYLE",
    title = "Typography & Colors",
    description = "A balanced system built specifically for boutique luxury. We paired an organic, editorial serif typeface with a clean, tech-focused geometric sans-serif to create hierarchy and contrast.",
    systemLabel = "SYSTEM CONFIGURATION",
    fonts = [
        {
            tag: "Editorial Serif (Cormorant)",
            letters: "AaBb",
            description: "Used for high-level editorial headers, statement quotes, and section titles. Whispers prestige and captures an artisanal feel.",
            fontFamilyClass: "font-cormorant"
        },
        {
            tag: "Legibility Sans (Inter Stack)",
            letters: "AaBb",
            description: "Applied to body text, metadata tabs, values grids, and operational actions to preserve crisp legibility across screens.",
            fontFamilyClass: "font-sans"
        }
    ],
    colors = [
        {
            name: "Soft Silk White",
            hex: "#FAF7F2",
            description: "A clean, minimalistic white with a warm hint. Used for typography and accents to create elegant contrast.",
            colorValue: "#FAF7F2"
        },
        {
            name: "Obsidian Charcoal",
            hex: "#09090A",
            description: "A deep black with a subtle cool undertone. Maintained as the main background for spacious visual balance.",
            colorValue: "#09090A"
        },
        {
            name: "Champagne Gold",
            hex: "#C5A880",
            description: "A refined golden-beige tone symbolizing exclusivity. Applied to highlights, tags, and active states.",
            colorValue: "#C5A880"
        }
    ],
    style = {}
}: TypographyColorsBlockProps) {
    const bg = style.backgroundColor || "#09090A";
    const text = style.textColor || "#FAF7F2";
    const accent = style.accentColor || "#c5a880";

    return (
        <div 
            className="w-full px-6 md:px-12 lg:px-24"
            style={{
                backgroundColor: bg,
                color: text,
                paddingTop: style.paddingTop || "6rem",
                paddingBottom: style.paddingBottom || "6rem",
            }}
        >
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    
                    {/* Sticky left panel */}
                    <div className="lg:col-span-4 flex flex-col justify-between">
                        <div>
                            {subtitle && (
                                <span className="text-xs font-mono tracking-widest block mb-2" style={{ color: accent }}>
                                    {subtitle}
                                </span>
                            )}
                            {title && (
                                <h2 className="text-4xl font-light font-cormorant leading-tight text-white">
                                    {title}
                                </h2>
                            )}
                            {description && (
                                <p className="text-zinc-400 text-xs mt-4 font-light leading-relaxed font-sans max-w-sm">
                                    {description}
                                </p>
                            )}
                        </div>
                        {systemLabel && (
                            <div className="hidden lg:block font-mono text-[10px] text-zinc-600 mt-8">
                                {systemLabel}
                            </div>
                        )}
                    </div>

                    {/* Right Content */}
                    <div className="lg:col-span-8 space-y-12">
                        
                        {/* Font Stack Box */}
                        {fonts && fonts.length > 0 && (
                            <div className="border border-zinc-850 bg-[#0E0E10] rounded-2xl p-8 relative overflow-hidden">
                                <span className="text-[9px] font-mono text-zinc-500 uppercase block mb-6">TYPOGRAPHY STACK</span>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                                    {fonts.map((font, idx) => (
                                        <div 
                                            key={idx} 
                                            className={`space-y-2 ${idx > 0 ? 'border-t md:border-t-0 md:border-l border-zinc-900 pt-6 md:pt-0 md:pl-8' : ''}`}
                                        >
                                            {font.tag && <span className="text-[10px] font-mono uppercase" style={{ color: accent }}>{font.tag}</span>}
                                            {font.letters && (
                                                <div className={`text-6xl md:text-7xl font-light text-white ${font.fontFamilyClass || 'font-sans'}`}>
                                                    {font.letters}
                                                </div>
                                            )}
                                            {font.description && (
                                                <p className="text-zinc-400 text-[11px] leading-relaxed font-light">
                                                    {font.description}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Color Swatches Grid */}
                        {colors && colors.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {colors.map((color, idx) => (
                                    <div key={idx} className="bg-[#0E0E10] border border-zinc-850 rounded-xl p-6 flex flex-col justify-between h-48">
                                        <div className="flex justify-between items-start">
                                            <span className="text-[10px] font-mono text-zinc-500">HEX / {color.hex}</span>
                                            <div 
                                                className="w-5 h-5 rounded-full border border-zinc-700" 
                                                style={{ backgroundColor: color.colorValue }}
                                            />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-white">{color.name}</h3>
                                            <p className="text-zinc-400 text-[10px] leading-relaxed font-light mt-1.5">
                                                {color.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>

                </div>
            </div>
        </div>
    );
}
